// 097 -- Parent-side "My offers" page. Parents register standing offers
// scoped to a school they're affiliated with. Organisers browse these
// on /schools/:id/volunteers and reach out directly.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import type {
  UserSchoolAssociationPublic,
  VolunteerCategory,
  VolunteerOfferPublic,
} from "@dugads/shared";
import { VOLUNTEER_CATEGORIES } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { Plus, Trash2 } from "lucide-react";

const CATEGORY_LABELS: Record<VolunteerCategory, string> = {
  transport: "Transport",
  catering: "Catering",
  scoring: "Scoring",
  setup: "Setup",
  first_aid: "First aid",
  general: "General",
};

export default function MyVolunteerOffersPage() {
  const [offers, setOffers] = useState<VolunteerOfferPublic[]>([]);
  const [schools, setSchools] = useState<UserSchoolAssociationPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [offersRes, schoolsRes] = await Promise.all([
        api.getMyVolunteerOffers(),
        api.getSchoolAssociations(),
      ]);
      setOffers(offersRes.offers);
      setSchools(schoolsRes.associations.filter((a) => a.status === "confirmed"));
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  async function handleDelete(id: string) {
    if (!confirm("Delete this offer?")) return;
    try {
      await api.deleteMyVolunteerOffer(id);
      await reload();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete");
    }
  }

  async function handleToggleActive(offer: VolunteerOfferPublic) {
    try {
      await api.updateMyVolunteerOffer(offer.id, { active: !offer.active });
      await reload();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to update");
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="my-volunteer-offers" data-state="loading">Loading…</p>;

  return (
    <div data-page="my-volunteer-offers" data-state="ready" className="flex flex-col gap-6">
      <Breadcrumbs items={[{ label: "My profile", to: "/profile" }, { label: "Volunteer offers" }]} />
      <h1 className="text-2xl font-semibold">My volunteer offers</h1>
      <p className="text-sm text-muted-foreground">
        Tell organisers at your schools what you can help with. They'll browse offers when planning events and reach out directly. You can toggle offers on or off any time.
      </p>
      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {schools.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">
              You need at least one confirmed school association before you can register offers. Visit your profile to join a school.
            </p>
          </CardContent>
        </Card>
      ) : adding ? (
        <OfferForm schools={schools} onSaved={() => { setAdding(false); void reload(); }} onCancel={() => setAdding(false)} />
      ) : (
        <div>
          <Button size="sm" onClick={() => setAdding(true)} data-action="new-offer">
            <Plus className="mr-1 h-4 w-4" /> New offer
          </Button>
        </div>
      )}

      {offers.length === 0 ? (
        <p className="text-sm text-muted-foreground">You haven't registered any offers yet.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {offers.map((o) => (
            <Card key={o.id} data-row={o.id} data-active={o.active}>
              <CardHeader className="flex flex-row items-start justify-between gap-2">
                <div className="flex-1">
                  <CardTitle className="text-base">{o.title}</CardTitle>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {CATEGORY_LABELS[o.category]} · {o.school_name}
                    {!o.active && <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">Inactive</span>}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => handleToggleActive(o)} data-action="toggle-active">
                    {o.active ? "Deactivate" : "Reactivate"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(o.id)} data-action="delete">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              {o.description && (
                <CardContent>
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">{o.description}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function OfferForm({
  schools,
  onSaved,
  onCancel,
}: {
  schools: UserSchoolAssociationPublic[];
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [schoolId, setSchoolId] = useState(schools[0]?.school_id ?? "");
  const [category, setCategory] = useState<VolunteerCategory>("general");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      await api.createMyVolunteerOffer({
        school_id: schoolId,
        category,
        title,
        description: description || null,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof api.ApiError ? e.message : "Failed to create offer");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card data-region="new-offer-form">
      <CardHeader><CardTitle className="text-base">New offer</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="school_id">School</Label>
              <select
                id="school_id"
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={schoolId}
                onChange={(e) => setSchoolId(e.target.value)}
                required
                data-field="school_id"
              >
                {schools.map((s) => (
                  <option key={s.school_id} value={s.school_id}>{s.school_display_name || s.school_name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={category}
                onChange={(e) => setCategory(e.target.value as VolunteerCategory)}
                data-field="category"
              >
                {VOLUNTEER_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              required
              maxLength={100}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. I have a bakkie"
              data-field="title"
            />
          </div>
          <div>
            <Label htmlFor="description">Description (optional)</Label>
            <textarea
              id="description"
              rows={3}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Any details organisers might find useful"
              title="Offer description"
            />
          </div>
          {err && <p className="text-sm text-destructive" data-error>{err}</p>}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving} data-action="save-offer">
              {saving ? "Saving…" : "Save offer"}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
